import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db, auth } from '../../firebase/config'; 
import { 
    collection, 
    doc, 
    getDocs, 
    serverTimestamp,
    getDoc,
    query 
} from 'firebase/firestore';

// Importamos la utilidad de persistencia centralizada y auditoría
import { saveToFirestore } from '../../firebase/cleanPayload'; 

import { 
    parseCurrency, 
    formatCurrencyForInput, 
    formatDateForInput, 
    formatDateShort,
    mapMonedaToDB,      
    mapMonedaToVisual    
} from '../../utils/segurosUtils';

// Importación del Custom Hook de Bitácoras Centralizado (Pilar del Canvas)
import { useBitacoraTimeline } from '../../utils/useBitacoraTimeline';

// Importaciones del Contexto y Listas de apoyo
import { useSegurosData } from '../../context/SegurosDataContext';
import { cobranzaEstados, cobranzaEstadosDeuda, formaPago as formasPagoList } from '../../data/listasDesplegables';

/**
 * renderSafeValue - Helper local para asegurar que el 0 se muestre como texto.
 */
const renderSafeValue = (val) => {
    if (val === 0 || val === "0") return "0";
    if (val === undefined || val === null || val === "") return "-";
    return val;
};

/**
 * ModalCobranza - Gestión financiera de cuotas, cobranzas y saldos a favor.
 * Totalmente optimizado con el nuevo Hook de Bitácoras useBitacoraTimeline y diseño premium.
 */
const ModalCobranza = ({ show, onClose, onSuccess, poliza, clientData, initialData }) => {
    // --- 1. CONSUMO DEL CONTEXTO MAESTRO GLOBAL ---
    const { dbMasters } = useSegurosData();

    // --- 2. ESTADOS LOCALES ---
    const [formData, setFormData] = useState({});
    const [newObservacion, setNewObservacion] = useState({ comentario: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cuotasExistentes, setCuotasExistentes] = useState([]); 
    const [fullClientData, setFullClientData] = useState(null);

    // --- 3. INICIALIZACIÓN DEL HOOK CENTRAL DE BITÁCORAS ---
    const {
        items: observaciones,
        loading: loadingObservaciones,
        editingId: editingObservacionId,
        setEditingId: setEditingObservacionId,
        fetchNotes: fetchObservaciones,
        addLocalNote,
        updateLocalNote,
        removeLocalNote,
        persistBatchChanges
    } = useBitacoraTimeline('comentario');

    const estadosGestionInicial = useMemo(() => {
        const excluidos = ['COBRADA', 'DEBITADA', 'ACREDITADA'];
        return cobranzaEstados.filter(e => !excluidos.includes(e.toUpperCase()));
    }, []);

    const resolvedIds = useMemo(() => {
        let cId = initialData?.clienteId || poliza?.clienteId || clientData?.id;
        let pId = initialData?.polizaId || poliza?.id;

        if ((!cId || !pId) && initialData?.path) {
            const parts = initialData.path.split('/');
            if (parts.length >= 6) {
                if (!cId) cId = parts[3];
                if (!pId) pId = parts[5];
            }
        }
        return { cId, pId };
    }, [initialData, poliza, clientData]);

    // Ruta de la subcolección de observaciones comercial para el Hook
    const observacionesPath = useMemo(() => {
        const { cId, pId } = resolvedIds;
        return (cId && pId && initialData?.id)
            ? `public/data/clientes/${cId}/polizas/${pId}/cobranza/${initialData.id}/observaciones`
            : null;
    }, [resolvedIds, initialData?.id]);

    // --- 4. CARGA DE COBRANZAS HISTÓRICAS Y CLIENTES (Soporte Multi-sesión) ---
    useEffect(() => {
        const fetchData = async () => {
            const { cId, pId } = resolvedIds;
            if (!cId || !pId) return;

            try {
                // Cuotas existentes de la póliza para vinculación de saldos a favor
                const qCuotas = query(collection(db, `public/data/clientes/${cId}/polizas/${pId}/cobranza`));
                const snapCuotas = await getDocs(qCuotas);
                
                const lista = snapCuotas.docs
                    .filter(d => d.id !== initialData?.id)
                    .map(d => {
                        const data = d.data();
                        const infoParts = [];
                        if (data.cuota !== undefined && data.cuota !== null) infoParts.push(`Cuota: ${data.cuota}`);
                        if (data.nroRecibo !== undefined && data.nroRecibo !== null) infoParts.push(`Rec: ${data.nroRecibo}`);
                        if (data.endoso !== undefined && data.endoso !== null) infoParts.push(`End: ${data.endoso}`);
                        
                        const label = infoParts.length > 0 ? infoParts.join(' | ') : `Registro del ${formatDateShort(data.fechaCarga)}`;
                        
                        return { 
                            id: d.id, 
                            cuotaNum: Number(data.cuota) || 0,
                            fechaMs: data.fechaCarga?.seconds || 0,
                            label: `${label} - (${formatCurrencyForInput(data.importe)})` 
                        };
                    })
                    .sort((a, b) => {
                        if (a.cuotaNum !== b.cuotaNum) return a.cuotaNum - b.cuotaNum;
                        return a.fechaMs - b.fechaMs;
                    });

                setCuotasExistentes(lista);

                // Recuperación de la ficha extendida del cliente
                const clientSnap = await getDoc(doc(db, 'public/data/clientes', cId));
                if (clientSnap.exists()) {
                    setFullClientData({ id: clientSnap.id, ...clientSnap.data() });
                }

            } catch (error) { 
                console.error("Error al cargar datos asíncronos en cobranzas:", error); 
            }
        };
        if (show) fetchData();
    }, [show, initialData, poliza, clientData, resolvedIds]);

    // --- 5. HIDRATACIÓN DINÁMICA DE LA DEUDA Y GESTIÓN DEL HOOK ---
    useEffect(() => {
        if (show) {
            if (initialData) {
                setFormData({
                    ...initialData,
                    fechaCarga: formatDateForInput(initialData.fechaCarga),
                    fechaReclamo: formatDateForInput(initialData.fechaReclamo),
                    fechaCobro: formatDateForInput(initialData.fechaCobro),
                    importe: formatCurrencyForInput(initialData.importe),
                    moneda: mapMonedaToVisual(initialData.moneda || poliza?.moneda || '$'),
                    nroRecibo: (initialData.nroRecibo !== null && initialData.nroRecibo !== undefined) ? String(initialData.nroRecibo) : '',
                    cuota: (initialData.cuota !== null && initialData.cuota !== undefined) ? String(initialData.cuota) : '',
                    endoso: (initialData.endoso !== null && initialData.endoso !== undefined) ? String(initialData.endoso) : '',
                    intentoCobro: (initialData.intentoCobro !== null && initialData.intentoCobro !== undefined) ? String(initialData.intentoCobro) : '',
                    hasSaldoAFavor: initialData.hasSaldoAFavor || false,
                    saldoAFavorReferencia: initialData.saldoAFavorReferencia || '',
                    saldoAFavorImporte: formatCurrencyForInput(initialData.saldoAFavorImporte || 0),
                    saldoAFavorEstado: initialData.saldoAFavorEstado || '',
                    saldoAFavorCuotaAplicada: initialData.saldoAFavorCuotaAplicada || '',
                    mostrarSeccionCobro: !!(initialData.quienCobro || initialData.fechaCobro || ['COBRADA', 'DEBITADA', 'ACREDITADA'].includes(String(initialData.estado || '').toUpperCase()))
                });

                // Cargamos de manera síncrona las observaciones utilizando el Hook
                if (observacionesPath) {
                    fetchObservaciones(observacionesPath);
                }
            } else {
                setFormData({
                    fechaCarga: new Date().toISOString().substring(0, 10),
                    endoso: '', cuota: '', nroRecibo: '', 
                    moneda: mapMonedaToVisual(poliza?.moneda || '$'), 
                    importe: '',
                    estado: 'Pendiente', estadoDeuda: '', formaPago: '', quienInformo: '',
                    fechaReclamo: '', motivoDeuda: '', intentoCobro: '', quienCobro: '', fechaCobro: '',
                    medioPagoReherido: false, mostrarSeccionCobro: false,
                    hasSaldoAFavor: false, saldoAFavorReferencia: '', saldoAFavorImporte: '',
                    saldoAFavorEstado: '', saldoAFavorCuotaAplicada: ''
                });
                // Si es un alta nueva, limpiamos el Hook para que empiece de cero
                fetchObservaciones(null);
            }
            setNewObservacion({ comentario: '' });
            setEditingObservacionId(null);
        }
    }, [initialData, show, poliza, observacionesPath, fetchObservaciones]);

    // --- 6. MEMORIZACIÓN Y ORDENAMIENTO DE LISTAS (useMemo) ---
    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    const sortedFormasPago = useMemo(() => {
        return [...formasPagoList].sort((a, b) => a.localeCompare(b));
    }, []);

    const listaMediosPago = useMemo(() => {
        const clienteActual = fullClientData || clientData || {};
        const normalizedForma = String(formData.formaPagoCobro || '').trim().toUpperCase();
        
        if (normalizedForma.includes('TARJETA')) {
            return (clienteActual.tarjetas || []).map(t => {
                const numStr = String(t.numeroTarjeta || t.numero || '');
                const last4 = numStr.slice(-4) || 'XXXX';
                return { 
                    id: `${t.marca} - ${t.banco} - ${last4}`, 
                    label: `${t.marca || 'TARJETA'} - ${t.banco || 'S/D'} - XXXX ${last4}`.toUpperCase() 
                };
            });
        }
        if (normalizedForma === 'CBU') {
            return (clienteActual.cbus || []).map(c => {
                const cbuStr = String(c.numeroCbu || c.cbu || c.numero || '');
                const last6 = cbuStr.slice(-6) || '000000';
                return { 
                    id: `${c.banco} - ${last6}`, 
                    label: `${c.banco || 'BANCO'} - **** ${last6}`.toUpperCase() 
                };
            });
        }
        return [];
    }, [formData.formaPagoCobro, fullClientData, clientData]);

    // --- 7. ACCIONES Y HANDLERS REDISEÑADOS CON EL HOOK ---
    const handleImporteChange = (e, fieldName = 'importe') => {
        let value = e.target.value.replace(/\D/g, ''); 
        if (value) {
            const number = Number(value) / 100;
            setFormData(prev => ({ ...prev, [fieldName]: formatCurrencyForInput(number) }));
        } else { 
            setFormData(prev => ({ ...prev, [fieldName]: '' })); 
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => {
            const newState = { ...prev, [name]: type === 'checkbox' ? checked : value };

            if (name === 'mostrarSeccionCobro' && !checked) {
                newState.quienCobro = '';
                newState.fechaCobro = '';
                newState.medioPagoReherido = false;
                newState.formaPagoCobro = '';
                newState.medioPago = '';
                newState.hasSaldoAFavor = false;
                newState.saldoAFavorReferencia = '';
                newState.saldoAFavorImporte = '';
                newState.saldoAFavorEstado = '';
                newState.saldoAFavorCuotaAplicada = '';
                
                const estadosFinalizados = ['COBRADA', 'DEBITADA', 'ACREDITADA'];
                if (estadosFinalizados.includes(String(prev.estado || '').toUpperCase())) {
                    newState.estado = 'Pendiente';
                }
            }
            return newState;
        });
    };

    const handleNumericChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value.replace(/\D/g, '') }));
    };

    // Agregar o modificar una nota delegando de forma síncrona en el Hook
    const handleAddOrUpdateObservacion = () => {
        if (!newObservacion.comentario.trim()) return;

        if (editingObservacionId) {
            updateLocalNote(editingObservacionId, null, newObservacion.comentario);
            setEditingObservacionId(null);
        } else {
            addLocalNote(null, newObservacion.comentario);
        }
        setNewObservacion({ comentario: '' }); 
    };

    const handleEditObservacion = (obs) => {
        setEditingObservacionId(obs.id);
        setNewObservacion({ comentario: obs.comentario || '' });
    };

    const handleRemoveObservacion = (idToDelete) => {
        removeLocalNote(idToDelete, observacionesPath);
    };

    const getNumericValueOrNull = (val) => {
        if (val === undefined || val === null || String(val).trim() === '') return null;
        const num = Number(val);
        return isNaN(num) ? null : num;
    };

    // --- 8. PROCESAMIENTO Y GUARDADO FINAL TRANSACCIONAL ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            const { cId, pId } = resolvedIds;
            if (!cId || !pId) throw new Error("Faltan identificadores de Cliente o Póliza.");

            const payload = { 
                ...formData,
                clienteId: cId,
                clienteNombre: fullClientData?.nombre || poliza?.clienteNombre|| formData.clienteNombre || '',
                clienteApellido: fullClientData?.apellido || poliza?.clienteApellido|| formData.clienteApellido || '',
                clienteCuit: fullClientData?.cuit || poliza?.clienteCuit || formData.clienteCuit || '',
                clienteDni: fullClientData?.dni || poliza?.clienteDni || formData.clienteDni || '',
                polizaId: pId,
                nPoliza: poliza?.nPoliza || formData.nPoliza || '',
                compania: poliza?.compania || formData.compania || '',
                companiaId: poliza?.companiaId || formData.companiaId || '',
                riesgo: poliza?.riesgo || formData.riesgo || '',
                riesgoId: poliza?.riesgoId || formData.riesgoId || '',
                subriesgo: poliza?.subriesgo || formData.subriesgo || '',
                canal: poliza?.canal || formData.canal || '',
                canalNombre: poliza?.canalNombre || poliza?.subcanal || formData.canalNombre || '',
                canalNombreId: poliza?.canalNombreId || formData.canalNombreId || '',
                subcanalNombre: poliza?.subcanalNombre || formData.subcanalNombre || '',
                subcanalNombreId: poliza?.subcanalNombreId || formData.subcanalNombreId || '',
                importe: parseCurrency(formData.importe),
                moneda: mapMonedaToDB(formData.moneda),
                saldoAFavorImporte: parseCurrency(formData.saldoAFavorImporte),
                intentoCobro: getNumericValueOrNull(formData.intentoCobro),
                cuota: getNumericValueOrNull(formData.cuota),
                nroRecibo: getNumericValueOrNull(formData.nroRecibo),
                endoso: getNumericValueOrNull(formData.endoso)
            };

            delete payload.vencimiento; 
            delete payload.mostrarSeccionCobro;
            delete payload.saldoAFavorRelacionRecibo; 

            const cobranzaPath = `public/data/clientes/${cId}/polizas/${pId}/cobranza`;
            const savedDoc = await saveToFirestore(cobranzaPath, payload, initialData?.id);

            // Guardado masivo y sincronizado de observaciones utilizando la API del Hook
            const finalObsPath = `public/data/clientes/${cId}/polizas/${pId}/cobranza/${savedDoc.id}/observaciones`;
            await persistBatchChanges(finalObsPath);
            
            onSuccess?.(initialData ? 'Cobranza actualizada correctamente.' : 'Nueva deuda cargada correctamente.');
            onClose();
        } catch (error) { 
            console.error("Error crítico al guardar cobranza:", error); 
        } finally { 
            setIsSubmitting(false); 
        }
    };

    if (!show) return null;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-sans">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-auto max-h-[95vh] flex flex-col overflow-hidden border border-gray-100 animate-fade-in transition-all duration-300">
                
                {/* HEADER DEL MODAL */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-blue-50/50 shrink-0">
                    <div className="flex items-center gap-3">
                        <span className="bg-blue-100 text-blue-600 p-2.5 rounded-xl shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </span>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 leading-none">{initialData ? 'Gestionar Cobranza' : 'Cargar Deuda'}</h3>
                            <p className="text-[10px] text-blue-800/80 font-black mt-1.5 tracking-wider uppercase select-none">Póliza: {initialData?.nPoliza || poliza?.nPoliza} • {initialData?.compania || poliza?.compania}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-slate-50 rounded-full">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* CUERPO DEL MODAL (Scroll optimizado) */}
                <div className="overflow-y-auto p-8 flex-grow bg-white scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                    <form id="cobranzaForm" onSubmit={handleSubmit} className="space-y-8">
                        
                        {/* SECCIÓN I: DETALLE DE CUOTA */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
                            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">Detalle de Cuota</legend>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2">
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Fecha Carga</label>
                                    <input type="date" name="fechaCarga" value={formData.fechaCarga || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required />
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Endoso</label>
                                    <input type="text" name="endoso" value={formData.endoso ?? ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" placeholder="Núm" />
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Cuota</label>
                                    <input type="text" name="cuota" value={formData.cuota ?? ''} onChange={handleNumericChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" placeholder="Núm" />
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">N° de Recibo</label>
                                    <input type="text" name="nroRecibo" value={formData.nroRecibo ?? ''} onChange={handleNumericChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" placeholder="Núm" />
                                </div>
                                
                                {/* Campo Importe/Moneda combinado */}
                                <div className="md:col-span-4 text-left">
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Importe *</label>
                                    <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden h-[46px] bg-white focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100 transition-all shadow-sm">
                                        <div className="bg-slate-50 px-3 h-full flex items-center border-r border-slate-100 shrink-0 select-none">
                                            <select 
                                                name="moneda" 
                                                value={formData.moneda || 'ARS $'} 
                                                onChange={handleChange}
                                                className="bg-transparent font-bold text-slate-700 outline-none cursor-pointer uppercase text-xs"
                                            >
                                                <option value="ARS $">ARS $</option>
                                                <option value="USD U$S">USD u$s</option>
                                            </select>
                                        </div>
                                        <input type="text" name="importe" value={formData.importe || ''} onChange={handleImporteChange} className="w-full px-4 outline-none font-mono font-bold text-slate-800 text-lg h-full" placeholder="0,00" required />
                                    </div>
                                </div>
                            </div>
                        </fieldset>

                        {/* SECCIÓN II: GESTIÓN DE DEUDA */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
                            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">Gestión de Deuda</legend>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Estado de Gestión *</label>
                                    <select 
                                        name="estado" 
                                        value={formData.estado || ''} 
                                        onChange={handleChange} 
                                        className={`w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold outline-none h-[46px] transition-all duration-300 ${
                                            formData.mostrarSeccionCobro 
                                                ? 'bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed opacity-60' 
                                                : 'bg-white text-blue-700 focus:border-blue-400 focus:ring-1 focus:ring-blue-100'
                                        }`} 
                                        required={!formData.mostrarSeccionCobro}
                                        disabled={formData.mostrarSeccionCobro}
                                    >
                                        <option value="">Seleccione...</option>
                                        {['COBRADA', 'DEBITADA', 'ACREDITADA'].includes(formData.estado?.toUpperCase()) && (
                                            <option value={formData.estado}>{formData.estado.toUpperCase()}</option>
                                        )}
                                        {estadosGestionInicial.map(e => <option key={e} value={e}>{e}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Estado de Deuda</label>
                                    <select name="estadoDeuda" value={formData.estadoDeuda || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all">
                                        <option value="">Seleccione...</option>
                                        {cobranzaEstadosDeuda?.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Intento de Cobro</label>
                                    <input type="text" name="intentoCobro" value={formData.intentoCobro ?? ''} onChange={handleNumericChange} className="w-full border border-indigo-200 rounded-xl px-4 py-2.5 text-sm font-bold text-indigo-800 outline-none bg-white h-[46px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all font-mono" placeholder="0" />
                                </div>

                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Forma de Pago *</label>
                                    <select name="formaPago" value={formData.formaPago || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required>
                                        <option value="">Seleccione...</option>
                                        {sortedFormasPago.map(fp => <option key={fp} value={fp}>{fp}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">¿Quién Informó?</label>
                                    <select name="quienInformo" value={formData.quienInformo || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all">
                                        <option value="">Seleccione...</option>
                                        {sortedColaboradores.map(v => (
                                            <option key={v.id} value={v.nombre}>{v.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Fecha Reclamo</label>
                                    <input type="date" name="fechaReclamo" value={formData.fechaReclamo || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" />
                                </div>
                                <div className="md:col-span-3">
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Motivo</label>
                                    <input type="text" name="motivoDeuda" value={formData.motivoDeuda || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" placeholder="Escriba los motivos de la deuda o falta de pago..." />
                                </div>
                            </div>
                        </fieldset>

                        {/* SECCIÓN III: OBSERVACIONES Y SEGUIMIENTO COMERCIAL (Integrada con useBitacoraTimeline) */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
                            <legend className="px-3 text-lg font-bold text-slate-800 bg-white absolute -top-3.5 left-4 tracking-tight">Observaciones y Seguimiento</legend>
                            
                            <div className={`p-4 border rounded-xl bg-blue-50/30 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end shadow-sm ${
                                editingObservacionId ? 'border-orange-400 bg-orange-50/10' : 'border-blue-100'
                            }`}>
                                <div className="md:col-span-3">
                                    <div className="flex items-center justify-between border-b border-blue-50 pb-2 mb-2 select-none">
                                        <h4 className="text-xs font-black text-blue-800 uppercase tracking-widest">
                                            {editingObservacionId ? '✏️ Editando Nota' : 'Nueva Nota de Seguimiento'}
                                        </h4>
                                    </div>
                                    <label className="block text-[12px] font-bold text-slate-600 uppercase tracking-widest pl-1 mb-1 select-none">Comentario</label>
                                    <input type="text" name="comentario" value={newObservacion.comentario} onChange={(e) => setNewObservacion({...newObservacion, comentario: e.target.value})} className="w-full border border-slate-200 rounded-lg px-3 text-xs text-slate-800 outline-none bg-white h-9 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" placeholder="Detalle la conversación, descargo o compromiso de pago..." />
                                </div>
                                <button type="button" onClick={handleAddOrUpdateObservacion} className={`font-bold h-9 px-4 rounded-lg text-xs uppercase tracking-widest text-white shadow-md active:scale-95 transition-all w-full ${
                                    editingObservacionId ? 'bg-orange-500 hover:bg-orange-600 shadow-orange-100' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'
                                }`}>
                                    {editingObservacionId ? 'Actualizar' : 'Agregar'}
                                </button>
                            </div>

                            {/* TABLA HISTÓRICA */}
                            <div className="max-h-60 overflow-y-auto space-y-3 px-1 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                                {loadingObservaciones ? (
                                    <p className="text-center text-slate-400 italic text-sm py-4 animate-pulse uppercase tracking-wider font-bold">Cargando notas...</p>
                                ) : observaciones.length === 0 ? (
                                    <p className="text-center text-slate-400 italic text-sm py-4">Sin notas registradas</p>
                                ) : (
                                    observaciones.map((o) => (
                                        <div key={o.id} className={`p-4 rounded-xl border bg-white shadow-sm flex flex-col gap-2 transition-all duration-300 animate-fade-in ${
                                            editingObservacionId === o.id ? 'border-orange-400 bg-orange-50/50 ring-2 ring-orange-100' : 'border-slate-100 hover:border-blue-200'
                                        }`}>
                                            <div className="flex justify-between items-center select-none">
                                                <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest tabular-nums">
                                                    {o.fechaCreacion?.seconds 
                                                        ? new Date(o.fechaCreacion.seconds * 1000).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) 
                                                        : (o.fechaCreacion instanceof Date ? o.fechaCreacion.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : 'Pendiente...')}
                                                </span>
                                                <div className="flex gap-3">
                                                    <button type="button" onClick={() => handleEditObservacion(o)} className="text-blue-400 text-[10px] uppercase font-black hover:text-blue-600 transition-colors">Editar</button>
                                                    <button type="button" onClick={() => handleRemoveObservacion(o.id)} className="text-red-300 text-[10px] uppercase font-black hover:text-red-500 transition-colors">Eliminar</button>
                                                </div>
                                            </div>
                                            <p className="text-sm text-slate-600 leading-relaxed italic pr-4">{o.comentario}</p>
                                        </div>
                                    ))
                                )}
                            </div>
                        </fieldset>

                        {/* SECCIÓN IV: REGISTRO DE COBRO / INTERRUPTOR */}
                        <div className="pt-2 select-none">
                            <label className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                                formData.mostrarSeccionCobro 
                                    ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg' 
                                    : 'bg-white border-slate-100 text-slate-400 hover:border-emerald-200'
                            }`}>
                                <input type="checkbox" name="mostrarSeccionCobro" checked={formData.mostrarSeccionCobro || false} onChange={handleChange} className="w-5 h-5 text-emerald-500 rounded focus:ring-emerald-400 cursor-pointer border-slate-300" />
                                <div className="flex flex-col text-left">
                                    <span className="text-sm font-black uppercase tracking-widest leading-none">Habilitar registro de Cobro / Gestión Finalizada</span>
                                    <span className={`text-[10px] font-bold mt-1 ${formData.mostrarSeccionCobro ? 'text-emerald-100' : 'text-slate-400'}`}>Marque esta opción solo si el cliente ya abonó o posee saldo.</span>
                                </div>
                            </label>

                            {formData.mostrarSeccionCobro && (
                                <fieldset className="border border-emerald-200 rounded-xl p-6 relative bg-emerald-50/10 mt-6 animate-fade-in space-y-8 shadow-sm">
                                    <legend className="px-2 text-lg font-bold text-emerald-700 bg-white absolute -top-3.5 left-4 tracking-tight">Cobro Finalizado</legend>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                                        <div>
                                            <label className="block text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5 pl-1">Resultado de la Gestión *</label>
                                            <select name="estado" value={formData.estado || ''} onChange={handleChange} className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm font-bold text-emerald-700 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all" required>
                                                <option value="">Seleccione...</option>
                                                <option value="Cobrada">COBRADA</option>
                                                <option value="Debitada">DEBITADA</option>
                                                <option value="Acreditada">ACREDITADA</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5 pl-1">¿Quién Cobró? *</label>
                                            <select name="quienCobro" value={formData.quienCobro || ''} onChange={handleChange} className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm font-bold text-emerald-950 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all" required={formData.mostrarSeccionCobro}>
                                                <option value="">Seleccione...</option>
                                                {sortedColaboradores.map(v => (
                                                    <option key={v.id} value={v.nombre}>{v.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5 pl-1">Fecha de Cobro *</label>
                                            <input type="date" name="fechaCobro" value={formData.fechaCobro || ''} onChange={handleChange} className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm font-bold text-emerald-950 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all" required={formData.mostrarSeccionCobro} />
                                        </div>
                                    </div>

                                    {/* Medio de Pago Adicional */}
                                    <div className={`p-5 rounded-xl border transition-all duration-300 ${formData.medioPagoReherido ? 'bg-emerald-50/50 border-emerald-400 shadow-md' : 'bg-white border-slate-100'}`}>
                                        <label className="flex items-center gap-3 cursor-pointer mb-1 select-none">
                                            <input type="checkbox" name="medioPagoReherido" checked={formData.medioPagoReherido || false} onChange={handleChange} className="w-5 h-5 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500" />
                                            <span className="text-sm font-black text-emerald-900 uppercase">Medio de Pago requerido</span>
                                        </label>
                                        {formData.medioPagoReherido && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5 animate-fade-in text-left">
                                                <div>
                                                    <label className="block text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1.5 pl-1">Forma de Pago Utilizada</label>
                                                    <select name="formaPagoCobro" value={formData.formaPagoCobro || ''} onChange={handleChange} className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all">
                                                        <option value="">Seleccione...</option>
                                                        {sortedFormasPago.map(f => <option key={f} value={f}>{f}</option>)}
                                                    </select>
                                                </div>
                                                {(formData.formaPagoCobro && (formData.formaPagoCobro.toUpperCase().includes('TARJETA') || formData.formaPagoCobro.toUpperCase() === 'CBU')) && (
                                                    <div className="animate-fade-in">
                                                        <label className="block text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1.5 pl-1">Seleccionar Medio del Cliente</label>
                                                        <select name="medioPago" value={formData.medioPago || ''} onChange={handleChange} className="w-full border border-emerald-300 rounded-xl px-4 py-2.5 text-sm font-black text-emerald-900 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all">
                                                            <option value="">-- Seleccionar medio --</option>
                                                            {listaMediosPago.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                                                            {listaMediosPago.length === 0 && <option value="" disabled>No hay medios registrados en la ficha</option>}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Saldo a Favor (Accento Violeta/Índigo) */}
                                    <div className={`p-5 rounded-xl border transition-all duration-300 ${formData.hasSaldoAFavor ? 'bg-indigo-50 border-indigo-200 shadow-sm' : 'bg-white border-slate-100 hover:border-indigo-200'}`}>
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input type="checkbox" name="hasSaldoAFavor" checked={formData.hasSaldoAFavor || false} onChange={handleChange} className="w-5 h-5 text-indigo-500 rounded border-slate-300 focus:ring-indigo-500" />
                                            <div className="flex flex-col text-left">
                                                <span className={`text-sm font-black uppercase tracking-widest ${formData.hasSaldoAFavor ? 'text-indigo-900' : 'text-slate-500'}`}>Registrar Saldo a Favor</span>
                                                <span className={`text-[10px] font-bold mt-0.5 ${formData.hasSaldoAFavor ? 'text-indigo-400' : 'text-slate-400'}`}>Registra un crédito para aplicar a deudas.</span>
                                            </div>
                                        </label>
                                        {formData.hasSaldoAFavor && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 animate-fade-in text-left">
                                                <div>
                                                    <label className="block text-[11px] font-bold text-indigo-700 uppercase tracking-wide mb-1.5 pl-1">Referencia / Comprobante *</label>
                                                    <input type="text" name="saldoAFavorReferencia" value={formData.saldoAFavorReferencia || ''} onChange={handleChange} className="w-full border border-indigo-200 rounded-xl px-4 py-2.5 text-sm font-bold text-indigo-950 outline-none bg-white h-[46px] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all" placeholder="Ej: REC-001" required />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-indigo-700 uppercase tracking-wide mb-1.5 pl-1">Importe del Saldo *</label>
                                                    <div className="flex items-center border border-indigo-200 rounded-xl overflow-hidden h-[46px] bg-white focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-100 transition-all">
                                                        <div className="bg-indigo-50 border-r border-indigo-100 px-3 h-full flex items-center shrink-0">
                                                            <span className="font-bold text-indigo-700">{formData.moneda || 'ARS $'}</span>
                                                        </div>
                                                        <input type="text" name="saldoAFavorImporte" value={formData.saldoAFavorImporte || ''} onChange={(e) => handleImporteChange(e, 'saldoAFavorImporte')} className="w-full px-4 outline-none font-mono font-bold text-indigo-950 h-full" placeholder="0,00" required />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-indigo-700 uppercase tracking-wide mb-1.5 pl-1">Estado del Saldo *</label>
                                                    <select name="saldoAFavorEstado" value={formData.saldoAFavorEstado || ''} onChange={handleChange} className="w-full border border-indigo-200 rounded-xl px-4 py-2.5 text-sm font-bold text-indigo-950 outline-none bg-white h-[46px] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all" required>
                                                        <option value="">Seleccione...</option>
                                                        <option value="Pendiente">Pendiente</option>
                                                        <option value="Aplicado">Aplicado</option>
                                                    </select>
                                                </div>
                                                {formData.saldoAFavorEstado === 'Aplicado' && (
                                                    <div className="animate-fade-in text-left">
                                                        <label className="block text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5 pl-1">Vincular Aplicación a:</label>
                                                        <select name="saldoAFavorCuotaAplicada" value={formData.saldoAFavorCuotaAplicada || ''} onChange={handleChange} className="w-full border border-emerald-300 rounded-xl px-4 py-2.5 text-sm font-bold text-emerald-950 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all">
                                                            <option value="">-- Seleccionar registro destino --</option>
                                                            {cuotasExistentes.map(c => (
                                                                <option key={c.id} value={c.label}>{c.label}</option>
                                                            ))}
                                                            {cuotasExistentes.length === 0 && <option value="" disabled>No hay otros registros disponibles</option>}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </fieldset>
                            )}
                        </div>
                    </form>
                </div>

                {/* BOTONERA DE ACCIONES INFERIOR */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 shrink-0">
                    <button onClick={onClose} className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs hover:bg-slate-50 shadow-sm transition-all transform active:scale-95 select-none">Cancelar</button>
                    <button type="submit" form="cobranzaForm" disabled={isSubmitting} className={`px-8 py-2.5 rounded-full text-white font-bold transition-all uppercase text-xs transform active:scale-95 min-w-[180px] shadow-lg select-none ${
                        formData.mostrarSeccionCobro 
                            ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-100' 
                            : 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'
                    }`}>
                        {isSubmitting ? 'Guardando...' : 'Confirmar cambios'}
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default ModalCobranza;